import type { ReactNode } from 'react';
import { EmptyState } from '../empty-state';
import { LogInIcon, RefreshCwIcon, TriangleAlertIcon, WifiOffIcon } from '../../../icons';

/**
 * Telas GLOBAIS de erro sistêmico (404 / 500 / sem conexão / sessão expirada)
 * — fonte ÚNICA da plataforma (EU-636/EU-711 no App Candidato; EU-686 RN-21
 * promoveu para cá com a propriedade de PLATAFORMA, como no Figma da
 * biblioteca publicada). É proibido criar cópia local por fluxo/tela.
 *
 * `platform` seleciona a copy/ações de cada produto:
 *  - `candidate`  — copies EU-636/EU-711 (frames Errors → Handoff 🏁).
 *  - `consultants` — copies EU-686 RN-20 (arquivo Errors, 17001-665/662/659/654).
 *
 * As AÇÕES chegam por handler (o app decide navegar/recarregar/logar); layout
 * (menu lateral presente ou não — RN-24/25) também é responsabilidade do app:
 * estas telas só ocupam o container em que forem montadas.
 */
export type ErrorPlatform = 'candidate' | 'consultants';

interface ErrorScreenAction {
  label: string;
  onClick: () => void;
}

export interface ErrorScreenProps {
  icon: ReactNode;
  title: string;
  body: string;
  primary: ErrorScreenAction;
  secondary?: ErrorScreenAction;
  /**
   * EU-747 — terceira saída, discreta (ghost). Existe para a tela de servidor do
   * candidato oferecer retomada da inscrição SEM perder "Voltar ao início".
   */
  tertiary?: ErrorScreenAction;
  testId: string;
  /** Tom do ícone (default: destructive). */
  tone?: 'destructive' | 'muted';
  /** `outline` p/ telas cujo único CTA é de contorno (sem conexão — OFF-002). */
  primaryVariant?: 'default' | 'outline';
}

export function ErrorScreen({
  icon,
  title,
  body,
  primary,
  secondary,
  tertiary,
  testId,
  tone = 'destructive',
  primaryVariant = 'default',
}: ErrorScreenProps) {
  // DEV-277 — o miolo (ícone + título + corpo + ações) é o `EmptyState` do DS;
  // aqui só se acrescenta a ocupação da tela inteira das telas globais.
  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background px-5">
      <EmptyState
        testId={testId}
        tone={tone === 'muted' ? 'neutral' : 'destructive'}
        icon={icon}
        title={title}
        body={body}
        primary={primary}
        secondary={secondary}
        tertiary={tertiary}
        primaryVariant={primaryVariant}
      />
    </div>
  );
}

/** 404 — rota/recurso inexistente. */
export function NotFoundScreen({
  platform = 'candidate',
  onGoHome,
}: {
  platform?: ErrorPlatform;
  onGoHome: () => void;
}) {
  const consultants = platform === 'consultants';
  return (
    <ErrorScreen
      testId="error-screen-not-found"
      tone="muted"
      icon={<TriangleAlertIcon className="size-6 text-muted-foreground" />}
      title="Página não encontrada"
      body={
        consultants
          ? 'Essa página pode ter sido removida ou o link que você usou está incorreto.'
          : 'A página que você tentou abrir não existe ou foi movida.'
      }
      primary={{
        label: consultants ? 'Ir para página inicial' : 'Voltar ao início',
        onClick: onGoHome,
      }}
    />
  );
}

/** 500 — erro de servidor. Consultants (RN-20): "Falar com o suporte" + retry. */
export function ServerErrorScreen({
  platform = 'candidate',
  onRetry,
  onGoHome,
  onSupport,
  onResume,
}: {
  platform?: ErrorPlatform;
  onRetry: () => void;
  /** candidate: CTA secundário "Voltar ao início". */
  onGoHome?: () => void;
  /** consultants: CTA primário "Falar com o suporte". */
  onSupport?: () => void;
  /**
   * EU-747 (candidate): retomada da inscrição — "Voltar às etapas". Com ele,
   * "Voltar ao início" desce para ação terciária: nunca é a única saída.
   */
  onResume?: () => void;
}) {
  if (platform === 'consultants') {
    return (
      <ErrorScreen
        testId="error-screen-server"
        icon={<TriangleAlertIcon className="size-6 text-destructive" />}
        title="Algo deu errado por aqui"
        body="Nossos servidores estão com problemas, mas a equipe já foi avisada. Tente de novo em instantes."
        primary={{ label: 'Falar com o suporte', onClick: onSupport ?? onRetry }}
        secondary={{ label: 'Tentar novamente', onClick: onRetry }}
      />
    );
  }
  return (
    <ErrorScreen
      testId="error-screen-server"
      icon={<TriangleAlertIcon className="size-6 text-destructive" />}
      title="Algo deu errado"
      body="Tivemos um problema para carregar esta página. Tente novamente em instantes."
      primary={{ label: 'Tentar novamente', onClick: onRetry }}
      secondary={
        onResume
          ? { label: 'Voltar às etapas', onClick: onResume }
          : onGoHome
            ? { label: 'Voltar ao início', onClick: onGoHome }
            : undefined
      }
      tertiary={onResume && onGoHome ? { label: 'Voltar ao início', onClick: onGoHome } : undefined}
    />
  );
}

/** Sem conexão — copy idêntica nas duas plataformas (frames No connection). */
export function NoConnectionScreen({ onRetry }: { platform?: ErrorPlatform; onRetry: () => void }) {
  return (
    <ErrorScreen
      testId="error-screen-no-connection"
      tone="muted"
      primaryVariant="outline"
      icon={<WifiOffIcon className="size-6 text-muted-foreground" />}
      title="Você está sem internet"
      body="Confira sua conexão e tente de novo."
      primary={{ label: 'Tentar novamente', onClick: onRetry }}
    />
  );
}

/**
 * DEV-95 (caso B) — o chunk da rota não chegou e um recarregamento automático já
 * foi gasto nesta aba. Tela SEPARADA da de erro de servidor de propósito:
 *
 * - o problema NÃO é do servidor, e a copy de servidor ("Tivemos um problema
 *   para carregar esta página") atribuía a ele uma falha que é de carregamento
 *   de asset — o candidato repassava isso ao suporte como fato;
 * - o problema NÃO é da conexão do candidato, então também não pode herdar a
 *   copy de sem-internet;
 * - o único CTA é um recarregamento DE VERDADE (`window.location.reload`), nunca
 *   o `reset()` do router: falha de módulo fica cacheada no *module map* do
 *   browser e re-tentar o mesmo `import()` falha em 0 ms sem tocar a rede.
 *
 * Sem CTA secundário: "Voltar ao início" é lido pelo candidato como perder o
 * progresso da inscrição, e é justamente o que ele estava tentando evitar.
 *
 * A copy é PROVISÓRIA — pendente de decisão de produto (pergunta aberta do
 * DEV-95). O requisito que ela cumpre: dizer o que fazer sem culpar a conexão do
 * candidato nem o servidor.
 */
export function StaleVersionScreen({
  onReload,
}: {
  platform?: ErrorPlatform;
  onReload: () => void;
}) {
  return (
    <ErrorScreen
      testId="error-screen-stale-version"
      tone="muted"
      icon={<RefreshCwIcon className="size-6 text-muted-foreground" />}
      title="Recarregue para continuar"
      body="Uma parte desta página não terminou de carregar. Recarregar resolve — seu progresso está salvo."
      primary={{ label: 'Recarregar a página', onClick: onReload }}
    />
  );
}

/** Sessão expirada. Consultants (RN-20/24): sem menu (o app decide o layout). */
export function SessionExpiredScreen({
  platform = 'candidate',
  onLogin,
  onGoHome,
}: {
  platform?: ErrorPlatform;
  onLogin: () => void;
  /** consultants: CTA primário "Ir para página inicial". */
  onGoHome?: () => void;
}) {
  if (platform === 'consultants') {
    return (
      <ErrorScreen
        testId="error-screen-session-expired"
        tone="muted"
        icon={<LogInIcon className="size-6 text-muted-foreground" />}
        title="Você foi desconectado"
        body="Por segurança, a gente desconecta sua conta depois de um tempo sem uso. É rapidinho entrar de novo."
        primary={{ label: 'Ir para página inicial', onClick: onGoHome ?? onLogin }}
        secondary={{ label: 'Fazer login', onClick: onLogin }}
      />
    );
  }
  return (
    <ErrorScreen
      testId="error-screen-session-expired"
      tone="muted"
      icon={<LogInIcon className="size-6 text-muted-foreground" />}
      title="Sua sessão expirou"
      body="Por segurança, entre novamente para continuar de onde parou."
      primary={{ label: 'Entrar', onClick: onLogin }}
    />
  );
}
